import { inject, Injectable } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AuthProvider } from './auth-provider.interface';
import { AuthRequiredPermissions } from './auth-required-permissions';
import { LocalStorageService } from '../storage/local-storage.service';
import { IDToken, UserClaims } from '@okta/okta-auth-js';

@Injectable({
  providedIn: 'root',
})
export class PrincipalAuthProvider implements AuthProvider {
  private readonly oktaAuth = inject(OKTA_AUTH);
  private readonly localStorageService = inject(LocalStorageService);

  async signIn(): Promise<boolean> {
    const defaultSignIn = await this.signInDefault();
    return defaultSignIn ? this.signInPrincipal() : Promise.resolve(false);
  }

  private async signInDefault(): Promise<boolean> {
    const isAuthenticated = await this.isAuthenticated();
    const cachedRequiredPermissions = this.getCachedRequiredPermissions();

    if (isAuthenticated && cachedRequiredPermissions) {
      return Promise.resolve(true);
    }

    await this.oktaAuth.signInWithRedirect({
      scopes: ['openid', 'required_permissions'],
      state: 'defaultRedirect',
    });
    return Promise.resolve(false);
  }

  private async signInPrincipal(): Promise<boolean> {
    const requiredPermissions = this.localStorageService.get(
      'auth-required-permissions',
    ) as AuthRequiredPermissions;

    const principalToken = await this.oktaAuth.tokenManager.getTokens();
    const currentScopes = principalToken?.accessToken?.scopes ?? [];

    if (currentScopes.includes('offline_access')) {
      return Promise.resolve(true);
    }

    await this.oktaAuth.signInWithRedirect({
      scopes: [
        'email',
        'profile',
        'openid',
        'offline_access',
        ...requiredPermissions.oasRequiredPermissions,
      ],
      state: 'principalRedirect',
    });
    return Promise.resolve(false);
  }

  isAuthenticated(): Promise<boolean> {
    return this.oktaAuth.isAuthenticated();
  }

  getAccessToken(): Promise<string | null> {
    return this.oktaAuth.getOrRenewAccessToken();
  }

  extractRequiredPermissions(claims?: UserClaims): AuthRequiredPermissions | null {
    if (!claims) {
      return null;
    }

    const userClaims = claims as any;
    if ('oasRequiredPermissions' in userClaims && 'msRequiredPermissions' in userClaims) {
      return {
        oasRequiredPermissions: userClaims['oasRequiredPermissions'],
        msRequiredPermissions: userClaims['msRequiredPermissions'],
        emRequiredPermissions: userClaims['emRequiredPermissions'],
      };
    }
    return null;
  }

  getCachedRequiredPermissions(): AuthRequiredPermissions | null {
    return this.localStorageService.get('auth-required-permissions') as AuthRequiredPermissions;
  }

  async parseFromUrl(): Promise<void> {
    const tokenContainer = await this.oktaAuth.token.parseFromUrl();
    if (!tokenContainer?.tokens) {
      return;
    }
    this.oktaAuth.tokenManager.setTokens(tokenContainer.tokens);

    // cache required permissions if any
    const requiredPermissions = this.extractRequiredPermissions(
      tokenContainer.tokens.idToken?.claims,
    );
    if (requiredPermissions) {
      localStorage.setItem('auth-required-permissions', JSON.stringify(requiredPermissions));
    }
  }

  logout(): Promise<boolean> {
    return this.oktaAuth.signOut({
      postLogoutRedirectUri: window.location.origin,
      clearTokensBeforeRedirect: true,
    });
  }

  clear(): void {
    this.oktaAuth.tokenManager.clear();
  }

  async getScopes(): Promise<string[]> {
    const tokens = await this.oktaAuth.tokenManager.getTokens();
    return tokens?.accessToken?.scopes ?? [];
  }

  async getIdToken(): Promise<IDToken | undefined> {
    const tokens = await this.oktaAuth.tokenManager.getTokens();
    return tokens?.idToken;
  }
}
